import { Database, Zap } from '@tamagui/lucide-icons';
import { BranchPowerMonitorData } from 'common';
import { useTranslation } from 'react-i18next';
import { XStack, YStack, Text, ScrollView, View, Separator, XStackProps } from 'tamagui';

import { InvalidConfiguration } from '../../../components/common/InvalidConfiguration';
import { LoadingOverlay } from '../../../components/common/LoadingOverlay';

export interface RawElectricalDataContentProps {
  data: BranchPowerMonitorData[];
  isValidConfiguration: boolean;
  isLoading: boolean;
}

export const RawElectricalDataContent = ({
  data,
  isValidConfiguration,
  isLoading,
}: RawElectricalDataContentProps) => (
    <YStack flex={1} gap="$4">
      <LoadingOverlay isVisible={isLoading} />

      {!isLoading ? (
        !isValidConfiguration ? (
          <InvalidConfiguration />
        ) : !data || data.length === 0 ? (
          <RawElectricalDataNoData />
        ) : (
          <YStack flex={1} gap="$3" marginBottom="$-5">
            <RawElectricalDataHeaders hide={isLoading} paddingRight="$3" />
            <ScrollView
              flex={1}
              contentContainerStyle={{ gap: '$3', paddingRight: '$3', paddingBottom: '$5' }}>
              {data.map((device, index) => (
                <RawElectricalDataRow
                  key={index}
                  device={device}
                  usagePercentage={device.percent}
                />
              ))}
            </ScrollView>
          </YStack>
        )
      ) : null}
    </YStack>
  );

const RawElectricalDataHeaders = ({ hide, ...rest }: XStackProps & { hide: boolean }) => {
  const { t } = useTranslation();

  if (hide) {
    return null;
  }

  return (
    <XStack alignItems="center" justifyContent="space-between" {...rest}>
      <View flexGrow={0.45} flexBasis={0} paddingHorizontal="$3.5">
        <Text fontSize="$5.5" color="$white12">
          {t('electrical.rawElectricalData.nameHeader')}
        </Text>
      </View>
      <View flexGrow={0.4} flexBasis={0} paddingHorizontal="$3.5">
        <Text fontSize="$5.5" color="$white12">
          {t('electrical.rawElectricalData.energyHeader')}
        </Text>
      </View>
      <View flexGrow={0.15} flexBasis={0} paddingHorizontal="$3.5" alignItems="center">
        <Text fontSize="$5.5" color="$white12">
          {t('electrical.rawElectricalData.usageHeader')}
        </Text>
      </View>
    </XStack>
  );
};

const RawElectricalDataRow = ({
  device,
  usagePercentage,
}: {
  device: BranchPowerMonitorData;
  usagePercentage: number;
}) => {
  const { t } = useTranslation();

  return (
    <XStack
      alignItems="center"
      justifyContent="space-between"
      height={60}
      backgroundColor="$black12"
      borderWidth={1}
      borderColor="$transparentBorder"
      borderRadius="$3">
      <View flexGrow={0.45} flexBasis={0} paddingHorizontal="$3.5">
        <Text
          fontSize="$7.5"
          fontWeight="bold"
          color="$white12"
          numberOfLines={1}
          ellipsizeMode="tail">
          {device.name}
        </Text>
      </View>
      <Separator vertical height="100%" borderColor="$transparentBorder" />
      <View flexGrow={0.4} flexBasis={0} paddingHorizontal="$3.5">
        <XStack gap="$2">
          <Text fontSize="$7.5" color="$white12">
            {t('electrical.rawElectricalData.ampsValue', { value: device.branchCT.toFixed(1) })}
          </Text>
          <Separator vertical borderColor="$white12" />
          <Text fontSize="$7.5" color="$white12">
            {t('electrical.rawElectricalData.voltsValue', { value: device.voltage.toFixed(1) })}
          </Text>
        </XStack>
      </View>
      <Separator vertical height="100%" borderColor="$transparentBorder" />
      <View flexGrow={0.15} flexBasis={0} paddingHorizontal="$3.5" alignItems="center">
        <XStack
          alignItems="center"
          gap="$1"
          backgroundColor="$hgPrimary"
          paddingVertical="$1"
          paddingHorizontal="$1.5"
          borderRadius={100}>
          <Zap flexShrink={0} size="$1" color="$black12" />
          <Text fontSize="$5" fontWeight="bold" color="$black12">
            {t('electrical.rawElectricalData.usageValue', {
              percentage: Math.round(usagePercentage) > 0 ? Math.round(usagePercentage) : '<1',
            })}
          </Text>
        </XStack>
      </View>
    </XStack>
  );
};

const RawElectricalDataNoData = () => {
  const { t } = useTranslation();

  return (
    <XStack
      flex={1}
      gap="$3.5"
      alignItems="center"
      justifyContent="center"
      backgroundColor="$black12"
      borderRadius="$3"
      marginTop="$-2.5">
      <Database size="$2.5" />
      <Text fontSize="$7.5" fontWeight="bold" color="$white12">
        {t('electrical.rawElectricalData.noData')}
      </Text>
    </XStack>
  );
};
